/**
 * EngineRenderer — wraps THREE.WebGPURenderer with camera stack, resize handling,
 * and multi-camera rendering.
 */

import * as THREE from 'three/webgpu';
import { MAX_PIXEL_RATIO, DEFAULT_EXPOSURE } from '../shared/constants.js';
import { CameraStack, type CameraEntry } from './CameraStack.js';
import { Logger } from '../core/Logger.js';
import { PostProcessingPipeline, type PostProcessingConfig } from './PostProcessingPipeline.js';
import { PostPipelineSwap } from './PostPipelineSwap.js';
import {
  detectRuntimeCaps,
  setRuntimeGpuCaps,
  desiredRequiredFeatures,
  type RuntimeGpuCaps,
} from './RuntimeCaps.js';
import { createGpuTimer, type GpuTimerRendererLike } from './timing/GpuTimerFactory.js';
import { GpuProfilerAggregator } from './timing/GpuProfilerAggregator.js';
import type { GpuTimingSource } from './timing/GpuTimingSource.js';
import type { IGpuTimerBackend } from './timing/GpuTimerBackend.js';
import { setDepthConvention, installPolygonOffsetBackports } from './depth/depthBias.js';
import { installPrecompileShadowGuard } from './backports/threeR186.js';
import { installDirectLightSkip } from './backports/directLightSkip.js';
import { installBackgroundLast } from './backports/backgroundLast.js';

export interface RendererConfig {
  canvas: HTMLCanvasElement;
  antialias?: boolean;
  alpha?: boolean;
  powerPreference?: GPUPowerPreference;
  pixelRatio?: number;
  /**
   * Pre-created WebGL2 context to render into (three uses it as-is). Lets a
   * caller opt into context attributes three hardcodes away — e.g.
   * `preserveDrawingBuffer: true` so headless screenshot harnesses capture
   * real pixels (the compositor grabs AFTER the buffer swap). Pair with
   * `forceWebGL` so the WebGPU backend never races the provided GL context.
   */
  context?: WebGL2RenderingContext;
  /** Force the WebGL2 backend even where WebGPU is available. */
  forceWebGL?: boolean;
  /**
   * Ask three to render both XR eyes in one pass (OVR_multiview2) — a CONSTRUCTOR-time opt-in in r185. It only takes
   * effect on the WebXR layers path (a texture-array projection layer) and is off by default: three #32538 (the
   * right eye's projection) and #32151 (flicker) are open. Desktop rendering never reads it.
   */
  multiview?: boolean;
  /**
   * Request GPU timestamp queries so per-pass GPU time can be measured.
   *
   * MUST be decided before construction: three.js r182 copies this into the
   * renderer in its constructor (`this.trackTimestamp = parameters.trackTimestamp === true`)
   * and the backend snapshots it at ITS init. Flipping the flag afterwards is
   * silently ignored and every later resolveTimestampsAsync() warns.
   *
   * Off by default — timestamp queries are not free.
   */
  trackTimestamp?: boolean;
  /**
   * REVERSED floating-point depth (three r185.1 `reversedDepthBuffer`) — the shader-gaps depth study (2026-09-29):
   * on Atlas's 0.05 m / 120 km camera a standard 24-bit buffer steps ~7 cm at 250 m and ~1.2 m at 1 km, so a road,
   * decal or shoreline a few centimetres over its surface loses the depth test from a few hundred metres out
   * (`depth-bed-probe`: 7 of 12 tiles lost); reversed Depth32Float loses none. Honoured on the WEBGPU backend ONLY:
   * on WebGL2 three runs it through EXT_clip_control ZERO_TO_ONE while its own `getViewPosition` still maps depth to
   * [-1, 1] (every post pass that rebuilds positions would be wrong), and XR presents on WebGL2 with the XR
   * projection, which three never reverses. A WebGL2 fallback that took the flag has it turned back off at init.
   * Constructor-time: it cannot change for the renderer's life.
   */
  reversedDepth?: boolean;
}

export class EngineRenderer {
  readonly native: THREE.WebGPURenderer;
  readonly cameras = new CameraStack();
  private _isWebGPU = false;
  /** When true, renderFrame() skips the draw+submit entirely — used to hold
   *  ALL GPU submits during a world transition so the old area's teardown
   *  disposals can never collide with an in-flight command buffer
   *  ("used in submit while destroyed"). The DOM loading overlay stays up. */
  paused = false;
  private _caps: RuntimeGpuCaps | null = null;
  postProcessing: PostProcessingPipeline | null = null;
  private _gpuTiming: GpuProfilerAggregator | null = null;
  private _gpuTimerBackend: IGpuTimerBackend | null = null;

  /** GPU per-pass timing source, or null when timestamps were not requested. */
  get gpuTiming(): GpuTimingSource | null {
    return this._gpuTiming;
  }

  private constructor(renderer: THREE.WebGPURenderer) {
    this.native = renderer;
  }

  static async create(config: RendererConfig): Promise<EngineRenderer> {
    // Pre-probe the adapter so we can ask for every feature the engine uses
    // (subgroups, shader-f16, timestamp-query, …) right at device creation.
    // Three.js WebGPUBackend accepts a `requiredFeatures` option that it
    // forwards into `adapter.requestDevice`.
    let requiredFeatures: GPUFeatureName[] = [];
    // Texture-heavy materials (biome splatting: N albedos + N normal maps) blow
    // past WebGPU's DEFAULT 16-sampled-textures-per-stage device limit even when
    // the adapter supports far more (desktop GPUs report 48+). Lift the sampler/
    // texture limits to whatever THIS adapter actually provides — requesting the
    // adapter's own values can never fail the device request.
    let maxSampledTextures = 16;
    let maxSamplers = 16;
    // THE LIMIT LAW (coordinator review, 2026-09-27): this used to request a HARD-CODED 128 bytes per sample. A required
    // limit the adapter cannot provide FAILS the device request, and three then silently falls back to WebGL2 — so
    // any adapter reporting 32 or 64 lost WebGPU. Ask for the adapter's own value (≤ 128), like the samplers below.
    let maxColorBytes = 32;
    try {
      if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
        const preAdapter = await (navigator as any).gpu.requestAdapter({
          powerPreference: config.powerPreference ?? 'high-performance',
        });
        if (preAdapter) {
          requiredFeatures = desiredRequiredFeatures(preAdapter as GPUAdapter);
          const limits = (preAdapter as GPUAdapter).limits;
          maxSampledTextures = limits.maxSampledTexturesPerShaderStage ?? 16;
          maxSamplers = limits.maxSamplersPerShaderStage ?? 16;
          maxColorBytes = Math.min(128, limits.maxColorAttachmentBytesPerSample ?? 32);
        }
      }
    } catch {
      // Adapter probing can fail in fingerprint-hardened browsers; safe to continue.
    }

    // THE LIGHT-LOOP SKIP (render/backports/directLightSkip.ts, the shader sweep 2026-10-06): three r185.1 evaluates
    // every light's full BRDF for every lit fragment; the patch skips a light's BRDF where its irradiance is 0 (out of
    // range, back-facing, in shadow, dark). Pixel-identical (Atlas verify/light-skip-bed-probe.mjs); a global prototype
    // patch, so it goes in before any material builds. 'enginex:light-skip' = 'off' keeps three's.
    if (installDirectLightSkip()) Logger.info('Renderer', 'light-loop skip on (r185 PhysicalLightingModel.direct)');
    const renderer = new THREE.WebGPURenderer({
      canvas: config.canvas,
      context: config.context,
      forceWebGL: config.forceWebGL ?? false,
      multiview: config.multiview === true,
      antialias: config.antialias ?? true,
      alpha: config.alpha ?? false,
      powerPreference: config.powerPreference ?? 'high-performance',
      requiredFeatures,
      requiredLimits: {
        maxColorAttachmentBytesPerSample: maxColorBytes,
        maxSampledTexturesPerShaderStage: maxSampledTextures,
        maxSamplersPerShaderStage: maxSamplers,
      },
      trackTimestamp: config.trackTimestamp ?? false,
      reversedDepthBuffer: config.reversedDepth === true && config.forceWebGL !== true,
    } as any);

    await renderer.init();

    // THE DEPTH CONVENTION (shader gaps R5): reversed depth on WebGPU only — a WebGL2 fallback that took the flag
    // (EXT_clip_control present) is put back to standard before anything renders or allocates a depth texture.
    {
      const r = renderer as unknown as { reversedDepthBuffer?: boolean; backend?: { isWebGPUBackend?: boolean; state?: { setReversedDepth?: (b: boolean) => void } } };
      if (r.reversedDepthBuffer === true && r.backend?.isWebGPUBackend !== true) {
        r.reversedDepthBuffer = false;
        r.backend?.state?.setReversedDepth?.(false);
        Logger.warn('Renderer', 'reversed depth asked on the WebGL2 backend — kept STANDARD (three getViewPosition + XR are standard-only there)');
      }
      setDepthConvention(r.reversedDepthBuffer === true);
      if (r.reversedDepthBuffer === true) Logger.info('Renderer', 'reversed floating-point depth ON (Depth32Float, cleared to 0)');
      // three r185.1 polygon-offset bugs (render/depth/depthBias.ts): the pipeline key misses the offset (decals drew
      // with NO bias whenever their shader matched an earlier material's — standard depth too), and reversed depth
      // needs the sign flipped. Backported until three r187.
      const bp = installPolygonOffsetBackports(renderer);
      if (bp.cacheKey || bp.reversedSign) Logger.info('Renderer', `polygon-offset backports: pipeline key ${bp.cacheKey ? 'on' : 'off'}, reversed sign ${bp.reversedSign ? 'on' : 'off'}`);
    }
    // three r186 #33924 (render/backports/threeR186.ts): r185.1's compileAsync rendered every shadow cascade from its
    // per-object loop — a whole-scene render per cascade per warm call, on both backends. Backported until r186.
    if (installPrecompileShadowGuard(renderer)) Logger.info('Renderer', 'precompile shadow guard on (three #33924 backport)');
    // THE BACKGROUND LAST (render/backports/backgroundLast.ts, the shader sweep 2026-10-06): three draws a node background
    // FIRST with no depth test, so the sky shader ran on every pixel the ground then covered (on WebGL2 / XR a ~270-sample
    // atmosphere march per pixel). Drawn after the depth-writing opaques, depth-tested at the far plane: the same picture,
    // the sky shaded only where it shows. Not under reversed depth; 'enginex:background-last' = 'off' keeps three's order.
    if (!(renderer as unknown as { reversedDepthBuffer?: boolean }).reversedDepthBuffer && installBackgroundLast(renderer)) Logger.info('Renderer', 'background drawn after the opaques');

    const pixelRatio = Math.min(config.pixelRatio ?? window.devicePixelRatio, MAX_PIXEL_RATIO);
    renderer.setPixelRatio(pixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = DEFAULT_EXPOSURE;
    renderer.autoClear = false;

    const engineRenderer = new EngineRenderer(renderer);

    // Detect WebGPU vs WebGL fallback
    // By three's own flag, never the class NAME: a production bundle minifies `WebGPUBackend` (the shipped Atlas
    // bundle names it `RF`), so a name test read every production WebGPU page as WebGL2 (gaps wave, 2026-09-29).
    engineRenderer._isWebGPU = (renderer.backend as { isWebGPUBackend?: boolean } | undefined)?.isWebGPUBackend === true;

    // ── Name the pipeline that failed ──────────────────────────────────────
    // WebGPU reports a broken pipeline as `[Invalid RenderPipeline "X"] is
    // invalid due to a PREVIOUS error`, once per frame, forever — and the
    // previous error, the one carrying the actual WGSL diagnostic, is an
    // UNCAPTURED device error that nothing was listening for. So the console
    // fills with a symptom and never states the cause (2026-09-15).
    //
    // three labels pipelines `renderPipeline_${material.name || material.type}_${material.id}`,
    // so the symptom at least identifies the material; this prints the reason.
    // Deduplicated by message: a compile failure repeats every frame.
    try {
      const device = (renderer.backend as { device?: GPUDevice } | undefined)?.device;
      if (device && typeof device.addEventListener === 'function') {
        const seen = new Set<string>();
        device.addEventListener('uncapturederror', (ev: Event) => {
          const msg = String((ev as { error?: { message?: string } }).error?.message ?? ev);
          const key = msg.slice(0, 200);
          if (seen.has(key)) return;
          seen.add(key);
          Logger.error('Renderer', `WebGPU device error (first of its kind):\n${msg}`);
        });
        Logger.debug('Renderer', 'WebGPU uncaptured-error reporting armed');
      }
    } catch { /* WebGL2 fallback, or a backend without a device handle */ }

    // Detect GPU features (subgroups, f16, timestamp-query, etc.) and publish
    // them through the singleton so downstream systems can branch on them.
    engineRenderer._caps = await detectRuntimeCaps(renderer);
    setRuntimeGpuCaps(engineRenderer._caps);

    // Wire GPU per-pass timing. Both this stack and src/debug/gpuprofiler/
    // shipped unreferenced for several releases — this is the single place the
    // engine constructs a timer. Constructing a backend does NOT guarantee it
    // reports real numbers: on WebGPU (the engine's primary backend),
    // WebGPUGpuTimer.available is hardcoded false — WebGPU can only attach
    // timestamp writes via a pass descriptor's `timestampWrites` field at
    // pass-creation time, and this call site never has one to give it (it
    // brackets the opaque `renderer.render()` call in `_renderFrameInner`
    // below, not a raw encoder/pass). Rather than fake it, that backend always
    // degrades to NullGpuTimer and logs why (see
    // docs/perf/2026-07-25-engine-graphics-findings.md, finding 1b — an
    // IGpuTimerBackend interface change would be required to fix this for
    // real, which is out of scope here). WebGL2GpuTimer's matching defect
    // (wrong extension-name fallback + WebGL1-only query API) was fixed by
    // 3ac5867 — correct as coded, but unverified on real hardware from this
    // repo's dev/CI environment, which exposes no timer-query extension at all
    // (finding 1a). PerfHUD and the editor profiler show whatever the selected
    // backend actually reports; "GPU timing unavailable" remains the common
    // case on WebGPU, not a fixed default.
    if (config.trackTimestamp) {
      // GpuTimerRendererLike.native.backend is deliberately typed as the
      // minimal `{ device?: unknown }` shape to avoid importing three's
      // Backend class. Three's own `Renderer.backend` field is nominally
      // typed as the base `Backend` class (no public `device`), even though
      // the concrete instance is always WebGPUBackend/WebGLBackend at
      // runtime — so TS's weak-type check rejects the structural match here.
      // engineRenderer really does satisfy the runtime contract; the cast
      // is confined to this call site.
      const backend = createGpuTimer(engineRenderer as unknown as GpuTimerRendererLike);
      engineRenderer._gpuTimerBackend = backend;
      engineRenderer._gpuTiming = new GpuProfilerAggregator(backend);
      Logger.info('Renderer', `GPU timing ${backend.available ? 'enabled' : 'requested but unavailable'}`);
    }

    Logger.info('Renderer', `Initialized (${engineRenderer._isWebGPU ? 'WebGPU' : 'WebGL2'})`);

    return engineRenderer;
  }

  /** Runtime GPU capability matrix (feature flags + limits). */
  get caps(): RuntimeGpuCaps {
    // _caps is populated in create(); if access happens before that (tests
    // constructing a renderer manually), fall back to the singleton accessor.
    if (this._caps) return this._caps;
    // Lazy import via dynamic-safe access to avoid circular init.
    const lazy = (globalThis as any).__engineXRuntimeGpuCaps as RuntimeGpuCaps | undefined;
    if (lazy) return lazy;
    return {
      hasWebGPU: this._isWebGPU,
      backend: this._isWebGPU ? 'webgpu' : 'webgl2',
      features: new Set(),
      limits: {
        maxComputeWorkgroupSizeX: 256,
        maxComputeWorkgroupSizeY: 256,
        maxComputeWorkgroupSizeZ: 64,
        maxComputeInvocationsPerWorkgroup: 256,
        maxComputeWorkgroupsPerDimension: 65535,
        maxStorageBufferBindingSize: 128 * 1024 * 1024,
        maxStorageTexturesPerShaderStage: 4,
        maxUniformBufferBindingSize: 64 * 1024,
        maxBindGroups: 4,
        maxBindingsPerBindGroup: 640,
        maxTextureDimension2D: 8192,
        maxTextureDimension3D: 2048,
        maxTextureArrayLayers: 256,
        maxColorAttachments: 8,
        maxColorAttachmentBytesPerSample: 32,
      },
      adapterInfo: { vendor: '', architecture: '', device: '', description: '' },
      adapterType: 'unknown',
      hasF16: false,
      hasSubgroups: false,
      hasSubgroupsF16: false,
      hasTimestampQuery: false,
      hasIndirectFirstInstance: false,
      hasF32Filterable: false,
      hasR11G11B10RenderTarget: false,
      hasBC: false,
      hasASTC: false,
      hasETC2: false,
    };
  }

  get isWebGPU(): boolean {
    return this._isWebGPU;
  }

  private _lastSizeW = 0;
  private _lastSizeH = 0;
  /** Reusable scratch for renderer.getSize() in the per-camera render path. */
  private readonly _tmpSize = new THREE.Vector2();

  resize(width: number, height: number): void {
    // Guard against degenerate resize events (window minimize often delivers
    // 0-height). width/height = Infinity would silently propagate NaN through
    // every downstream projection-matrix consumer for the entire frame.
    // Must come BEFORE setSize — Three.js will collapse the framebuffer to 0×0
    // otherwise and stay there until the next valid resize.
    if (width <= 0 || height <= 0) {
      return;
    }

    this.native.setSize(width, height);

    // Update camera aspect ratios
    for (const entry of this.cameras.getEntries()) {
      const cam = entry.camera;
      if (cam instanceof THREE.PerspectiveCamera) {
        cam.aspect = width / height;
        cam.updateProjectionMatrix();
      } else if (cam instanceof THREE.OrthographicCamera) {
        // For orthographic cameras we keep the world-units-per-pixel ratio
        // constant across resizes. We need to scale both the width and the
        // height of the frustum — the previous implementation only remapped
        // left/right, freezing top/bottom at the original viewport height.
        const prevW = this._lastSizeW || width;
        const prevH = this._lastSizeH || height;
        const prevHalfH = cam.top;
        const prevHalfW = cam.right;
        // Guard against degenerate camera setup (e.g. top=0 ortho camera).
        // Without these guards the half-width or half-height collapses to
        // zero, freezing the frustum and making nothing render.
        const unitsPerPxX = prevW > 0 && prevHalfW > 0 ? (prevHalfW * 2) / prevW : 1;
        const unitsPerPxY = prevH > 0 && prevHalfH > 0 ? (prevHalfH * 2) / prevH : 1;
        const halfW = (width * unitsPerPxX) / 2;
        const halfH = (height * unitsPerPxY) / 2;
        cam.left = -halfW;
        cam.right = halfW;
        cam.top = halfH;
        cam.bottom = -halfH;
        cam.updateProjectionMatrix();
      }
    }
    this._lastSizeW = width;
    this._lastSizeH = height;
  }

  /**
   * Create a post-processing pipeline for a scene and camera.
   */
  createPostProcessing(
    scene: THREE.Scene,
    camera: THREE.Camera,
    config?: PostProcessingConfig,
  ): PostProcessingPipeline {
    this.postProcessing = new PostProcessingPipeline(
      this.native, scene, camera, config,
    );
    return this.postProcessing;
  }

  /** THE REBUILD FRAME swapper (2026-09-27): a replacement pipeline is built beside the live one (see PostPipelineSwap). */
  private _postSwap: PostPipelineSwap<PostProcessingPipeline> | null = null;
  /**
   * Rebuild the post pipeline WITHOUT a raw frame: the replacement sets up beside the live pipeline, which keeps
   * rendering, and is swapped in once ready; the old one is disposed two frames later (an in-flight encoder's
   * targets). With no live pipeline it goes live at once (same as `createPostProcessing`). A newer call supersedes
   * a pending one. Do NOT dispose the live pipeline before calling this — that is the raw-frame bug.
   */
  replacePostProcessing(
    scene: THREE.Scene,
    camera: THREE.Camera,
    config?: PostProcessingConfig,
  ): PostProcessingPipeline {
    this._postSwap ??= new PostPipelineSwap<PostProcessingPipeline>(
      () => this.postProcessing,
      (p) => { this.postProcessing = p; },
      (dispose) => {
        // two frames of grace (the Dawn destroyed-texture guard the CSM teardown uses) — or 250 ms, whichever comes
        // first: the window's rAF never fires in a hidden tab or while an XR session presents, and each held pipeline
        // pins full-resolution HDR targets until it does.
        let done = false;
        const run = (): void => {
          if (done) return;
          done = true;
          try { dispose(); } catch (err) { Logger.warn('Renderer', `post pipeline dispose failed: ${err}`); }
        };
        const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (f: () => void) => setTimeout(f, 16);
        raf(() => raf(run));
        setTimeout(run, 250);
      },
    );
    return this._postSwap.submit(new PostProcessingPipeline(this.native, scene, camera, config));
  }
  /** A replacement pipeline still setting up (null when none) — probes wait on it. */
  get pendingPostProcessing(): PostProcessingPipeline | null { return this._postSwap?.pendingPipeline ?? null; }

  /**
   * Render one frame, timing it as a single GPU pass named 'frame' when
   * timestamps are enabled.
   *
   * The body lives in _renderFrameInner because it has THREE exit paths (XR
   * early return, post-processing early return, and the camera loop). An
   * inline begin/end pair would leak an unclosed scope on two of them, and on
   * any throw — try/finally is what makes the scope balanced on every path.
   */
  renderFrame(scene: THREE.Scene): void {
    if (this.paused) return; // world-transition hold — no submits while the old world tears down
    const backend = this._gpuTimerBackend;
    if (!backend) {
      this._renderFrameInner(scene);
      return;
    }
    let opened = false;
    try {
      // Inside the try: a timer fault must never break the render loop.
      // Instrumentation is not allowed to take down the frame. `opened`
      // gates the matching endPass below — a scope that never opened must
      // never be closed.
      try {
        backend.beginPass('frame');
        opened = true;
      } catch (err) {
        Logger.warn('Renderer', `GPU timer beginPass failed — timing disabled this frame: ${err}`);
      }
      this._renderFrameInner(scene);
    } finally {
      if (opened) {
        try {
          backend.endPass('frame');
          // endFrame() only SCHEDULES async readback; tick() then pulls whichever
          // frame has actually resolved (typically 1-2 frames behind). Neither
          // stalls the pipeline.
          backend.endFrame();
          this._gpuTiming?.tick();
        } catch (err) {
          Logger.warn('Renderer', `GPU timer readback failed — timing disabled this frame: ${err}`);
        }
      }
    }
  }

  /**
   * Render all cameras in the stack against the given scene.
   */
  private _renderFrameInner(scene: THREE.Scene): void {
    const entries = this.cameras.getEntries();
    const renderer = this.native;

    // XR presentation: WebXRManager owns pose/stereo/framebuffer — it still needs
    // ONE render() call per frame, but the mono fullscreen post-processing quad and
    // overlay cameras (viewport/scissor) would corrupt the XR layer, so both are
    // bypassed. Falls back to entries[0] even when XRCameraOverrideSystem has
    // disabled the whole stack (its suppression targets the flat paths, not this one).
    if (renderer.xr?.isPresenting) {
      const primary = entries.find((e) => e.enabled !== false) ?? entries[0];
      if (primary) {
        renderer.setScissorTest(false);
        renderer.render(scene, primary.camera);
      }
      return;
    }

    // If post-processing is enabled and primary camera is active, use it
    if (this.postProcessing?.enabled && entries.length > 0 && entries[0].enabled !== false) {
      const primaryCamera = entries[0].camera;
      // Reset any scissor left active by a previous frame's overlay camera:
      // postProcessing.render() bypasses _renderCamera, so without this it
      // would clip the primary render to the stale scissor rect.
      renderer.setScissorTest(false);
      this.postProcessing.setCamera(primaryCamera);
      this.postProcessing.render();

      // Render remaining cameras (overlays, etc.) without post-processing
      for (let i = 1; i < entries.length; i++) {
        if (entries[i].enabled === false) continue;
        this._renderCamera(renderer, scene, entries[i]);
      }
      return;
    }

    for (const entry of entries) {
      if (entry.enabled === false) continue;
      this._renderCamera(renderer, scene, entry);
    }
  }

  private _renderCamera(
    renderer: THREE.WebGPURenderer,
    scene: THREE.Scene,
    entry: CameraEntry,
  ): void {
    // Viewport
    if (entry.viewport) {
      const vp = entry.viewport;
      // Use LOGICAL size (getSize), NOT domElement.width/height (physical =
      // logical × pixelRatio). Three.js r182's WebGPU setViewport/setScissor
      // take logical pixels and multiply the stored rect by _pixelRatio at
      // render time (Renderer _getFrameBufferTarget). Feeding physical pixels
      // here double-applies the DPR, pushing the rect off-screen whenever
      // pixelRatio≠1 (every HiDPI/Retina display).
      const sz = renderer.getSize(this._tmpSize);
      const w = sz.x;
      const h = sz.y;
      renderer.setViewport(vp.x * w, vp.y * h, vp.width * w, vp.height * h);
      renderer.setScissor(vp.x * w, vp.y * h, vp.width * w, vp.height * h);
      renderer.setScissorTest(true);
    } else {
      renderer.setScissorTest(false);
    }

    // Clear
    renderer.autoClear = false;
    if (entry.clearColor || entry.clearDepth || entry.clearStencil) {
      renderer.clear(
        entry.clearColor ?? false,
        entry.clearDepth ?? false,
        entry.clearStencil ?? false,
      );
    }

    // Layer mask
    if (entry.layers !== undefined) {
      entry.camera.layers.mask = entry.layers;
    }

    renderer.render(scene, entry.camera);
  }

  async requestScreenshot(mimeType = 'image/png', quality = 0.92): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.native.domElement.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to capture screenshot'));
        },
        mimeType,
        quality,
      );
    });
  }

  dispose(): void {
    this._postSwap?.dispose();
    this._postSwap = null;
    this.postProcessing?.dispose();
    this.postProcessing = null;
    // _gpuTimerBackend owns real GPU resources (WebGL2 query objects, or a
    // WebGPU query set + four buffers) — leaving it undisposed leaks them.
    // Guarded: a throwing backend must not break the rest of teardown.
    try { this._gpuTimerBackend?.dispose(); } catch { /* already gone */ }
    this._gpuTimerBackend = null;
    this._gpuTiming = null;
    this.native.dispose();
  }
}
